import React, { useEffect } from "react";
import { create } from "zustand";
import { Card, CardContent } from "@/components/ui/card";
import Loader from "@/components/ui/Loader";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import EmptyDatas from "components/ui/EmptyDatas";
import Table from "components/ui/my-data-table";
import useSWR from "swr";
import { columns } from "./data-table-column";
import DataTableToolbar from "./data-table-toolbar";

const PAGE_SIZE = 100;

export const useForm = create<{
  after: string | null;
  afters: string[];
  tid: string;
  status: number | string | null;
  type: string | null;
  includeChildren: boolean;
  mutate: (() => void) | null;
  setTid: (tid: string) => void;
  setStatus: (status: number | string | null) => void;
  setType: (type: string | null) => void;
  setIncludeChildren: (includeChildren: boolean) => void;
  setMutate: (mutate: () => void) => void;
  goAfter: (nextAfter: string | null) => void;
}>((set) => ({
  after: null,
  afters: [],
  tid: "",
  status: null,
  type: null,
  includeChildren: false,
  mutate: null,
  setTid: (tid) => set({ tid, after: null, afters: [] }),
  setStatus: (status) =>
    set({
      status:
        status === null || status === undefined || status === ""
          ? null
          : status,
      after: null,
      afters: [],
    }),
  setType: (type) =>
    set({
      type: type === null || type === undefined || type === "" ? null : type,
      after: null,
      afters: [],
    }),
  setIncludeChildren: (includeChildren) =>
    set({ includeChildren, after: null, afters: [] }),
  setMutate: (mutate) => set({ mutate }),
  goAfter: (nextAfter) =>
    set((state) => {
      if (nextAfter === null) {
        return { after: null };
      }
      const index = state.afters.findIndex((a) => a === nextAfter);
      if (index === -1) {
        return { after: nextAfter, afters: [...state.afters, nextAfter] };
      } else {
        return { after: nextAfter };
      }
    }),
}));

export const TemplatesTab = () => {
  const params = useForm((s) => {
    const { after, tid, status, type, includeChildren } = s;
    const hasStatus = status !== null && status !== undefined && status !== "";
    return {
      after: after || undefined,
      tid: tid || undefined,
      ...(hasStatus ? { status: Number(status) } : {}),
      type: type || undefined,
      limit: PAGE_SIZE,
      ...(includeChildren ? { includeChildren: true } : {}),
    };
  });

  const { data, isLoading, isValidating, mutate } = useSWR([
    "/spine-templates",
    params,
  ]);
  const nextAfter =
    data?.length >= PAGE_SIZE ? data[data.length - 1]?.id : undefined;
  const setMutate = useForm((s) => s.setMutate);

  useEffect(() => {
    setMutate(mutate);
  }, [mutate, setMutate]);

  const table = useReactTable({
    data: data || [],
    columns,
    enableRowSelection: false,
    getRowId: (row) => (row.id ? String(row.id) : String(row.index)),
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading || isValidating) {
    return (
      <Loader>Loading Spine templates...</Loader>
    );
  }
  if (!data) return <EmptyDatas />;

  return (
    <div className="flex flex-col h-[calc(100vh-150px)]">
      <div className="flex-shrink-0 mb-2">
        <DataTableToolbar mutate={mutate} nextAfter={nextAfter} />
      </div>
      <div className="flex-1 min-h-0">
        <Card className="h-full flex flex-col">
          <CardContent className="flex-1 overflow-hidden pl-4 pr-0 pb-0">
            <div className="h-full overflow-auto">
              <Table table={table} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
